import {
    ColorsElement,
    ColorsParameterMap,
    DARK_OVERRIDE_KEYS, DarkOverrideKey,
    DEFAULT_LIGHT_PALETTE_FALLBACK,
    PALETTE_COLOR_KEYS,
    PaletteColorKey,
} from '../types/colors.types.tsx';
import { ColorPaletteConfig, ColorWithTones } from '../types/colorHarmony.types.ts';
import { generateHarmony } from '@theme/utils/colorHarmony.ts';
import { generateTones } from '@theme/utils/colorTones.ts';
import { logInfo } from '@tools/LogsHandler.tsx';


const fallbackColorForIndex = (index: number): string =>
    DEFAULT_LIGHT_PALETTE_FALLBACK[index] ?? DEFAULT_LIGHT_PALETTE_FALLBACK[DEFAULT_LIGHT_PALETTE_FALLBACK.length - 1];

const normalizeHex = (value: string | undefined): string | null => {
    if (!value) {
        return null;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
};

export const getOrderedPaletteColors = (palette: ColorsParameterMap): string[] => {
    // Modern keys: --web-color-1, --web-color-2, --web-color-3 (base colors only)
    const modernKeys = ['--web-color-1', '--web-color-2', '--web-color-3'];

    // Check if palette uses modern keys
    const hasModernKeys = modernKeys.some(key => palette[key]?.codigo);

    if (hasModernKeys) {
        return modernKeys.map((key, index) => normalizeHex(palette[key]?.codigo) ?? fallbackColorForIndex(index));
    }

    // Fallback to legacy keys
    return PALETTE_COLOR_KEYS.map((key, index) => normalizeHex(palette[key]?.codigo) ?? fallbackColorForIndex(index));
};

export const getDerivedDarkPaletteColors = (palette: ColorsParameterMap): string[] => {
    const lightPalette = getOrderedPaletteColors(palette);
    const defaultDark = [...lightPalette].reverse();

    return DARK_OVERRIDE_KEYS.map((overrideKey, index) =>
        normalizeHex(palette[overrideKey]?.codigo) ?? defaultDark[index] ?? fallbackColorForIndex(index),
    );
};

const ensurePaletteStyleElement = (): HTMLStyleElement => {
    let element = document.getElementById('id_color_settings') as HTMLStyleElement | null;
    if (!element) {
        element = document.createElement('style');
        element.id = 'id_color_settings';
        document.head.appendChild(element);
    }
    return element;
};

const buildPaletteCss = (lightColors: readonly string[], darkColors: readonly string[]): string => {
    const toCssVars = (colors: readonly string[]) =>
        colors
            .map((color, index) => `  --web-color-${index + 1}: ${color};`)
            .join('\n');

    return [
        ':root, [data-theme="light"], .theme-light {',
        toCssVars(lightColors),
        '}',
        '[data-theme="dark"], .theme-dark {',
        toCssVars(darkColors),
        '}',
    ].join('\n');
};

const buildExtendedPaletteCss = (
    lightColors: readonly string[],
    darkColors: readonly string[],
    legacyExtras?: { light?: readonly string[]; dark?: readonly string[] },
): string => {
    const buildToneVars = (tones: ColorWithTones, index: number): string => {
        const prefix = `--web-color-${index + 1}`;
        return [
            `  ${prefix}: ${tones.base};`,
            `  ${prefix}-c1: ${tones.c1};`,
            `  ${prefix}-c2: ${tones.c2};`,
            `  ${prefix}-o1: ${tones.o1};`,
            `  ${prefix}-o2: ${tones.o2};`,
        ].join('\n');
    };

    const buildLegacyExtras = (colors?: readonly string[]) =>
        (colors ?? [])
            .slice(0, 3)
            .map((color, index) => `  --web-color-${index + 4}: ${color};`)
            .join('\n');

    const toCssVars = (colors: readonly string[]) =>
        colors
            .slice(0, 3)
            .map((color, index) => buildToneVars(generateTones(color), index))
            .join('\n');

    return [
        ':root, [data-theme="light"], .theme-light {',
        toCssVars(lightColors),
        buildLegacyExtras(legacyExtras?.light ?? lightColors.slice(3)),
        '}',
        '[data-theme="dark"], .theme-dark {',
        toCssVars(darkColors),
        buildLegacyExtras(legacyExtras?.dark ?? darkColors.slice(3)),
        '}',
    ].join('\n');
};

// Helper to detect modern keys
const isModernPalette = (palette: ColorsParameterMap) => Object.keys(palette).some(k => k.startsWith('--web-color-'));

export const applyPaletteToDom = (
    palette: ColorsParameterMap,
    options?: { useExtended?: boolean },
) => {
    if (typeof document === 'undefined') return;

    // Early return: Don't overwrite existing palette with empty one
    const paletteKeys = Object.keys(palette);
    if (paletteKeys.length === 0) {
        logInfo('[DEBUG] applyPaletteToDom - SKIPPED: empty palette, not overwriting');
        return;
    }

    const styleElement = ensurePaletteStyleElement();
    const hasModern = isModernPalette(palette);
    // logInfo('[DEBUG] applyPaletteToDom - isModernPalette:', hasModern, 'palette keys:', paletteKeys.slice(0, 10));

    // Modern Path: Direct Injection
    if (hasModern) {
        const cssVars = Object.entries(palette)
            .filter(([key, val]) => key.startsWith('--web-color-') && val?.codigo)
            .map(([key, val]) => `  ${key}: ${val!.codigo};`)
            .join('\n');

        // logInfo('[DEBUG] applyPaletteToDom - MODERN PATH - CSS vars count:', cssVars.split('\n').length);
        // logInfo('[DEBUG] applyPaletteToDom - MODERN PATH - CSS preview:', cssVars.substring(0, 500));

        // Apply globally (same for light/dark for now, as harmony system unifies them)
        styleElement.textContent = [
            ':root, [data-theme="light"], .theme-light, [data-theme="dark"], .theme-dark {',
            cssVars,
            '}'
        ].join('\n');
        return;
    }

    logInfo('[DEBUG] applyPaletteToDom - LEGACY PATH');

    // Legacy Path
    const light = getOrderedPaletteColors(palette);
    const dark = getDerivedDarkPaletteColors(palette);

    // Default to extended palette unless explicitly disabled
    const useExtended = options?.useExtended ?? true;

    styleElement.textContent = useExtended
        ? buildExtendedPaletteCss(light, dark)
        : buildPaletteCss(light, dark);
};

const buildLegacyTriplet = (tones: ColorWithTones[], variant: 'light' | 'dark'): string[] => {
    if (tones.length === 0) {
        return [];
    }

    if (tones.length === 1) {
        const [tone] = tones;
        return variant === 'light'
            ? [tone.c1, tone.c2, tone.base]
            : [tone.o1, tone.o2, tone.base];
    }

    if (tones.length === 2) {
        const [first, second] = tones;
        return variant === 'light'
            ? [first.base, second.base, first.c2]
            : [first.o2, second.o2, first.o1];
    }

    return variant === 'light'
        ? tones.slice(0, 3).map((tone) => tone.base)
        : tones.slice(0, 3).map((tone) => tone.o2);
};

export const applyHarmonyPaletteToDom = (config: ColorPaletteConfig) => {
    if (typeof document === 'undefined') return;
    const styleElement = ensurePaletteStyleElement();
    const count = config.colorCount;
    const customColors = [config.baseColor1, config.baseColor2, config.baseColor3]
        .map((color) => normalizeHex(color) ?? null)
        .filter((color): color is string => Boolean(color));
    const colors =
        config.harmony === 'custom'
            ? (customColors.length > 0 ? customColors : [config.baseColor1]).slice(0, count)
            : generateHarmony(config.baseColor1, config.harmony, count);

    const tones = colors.map((color) => generateTones(color));
    const legacyLight = buildLegacyTriplet(tones, 'light');
    const legacyDark = buildLegacyTriplet(tones, 'dark');
    styleElement.textContent = buildExtendedPaletteCss(colors, colors, {
        light: legacyLight,
        dark: legacyDark,
    });
};

export const mergePaletteWithRemote = (
    base: ColorsParameterMap,
    remote?: Record<string, ColorsElement | undefined> | null,
): ColorsParameterMap => {
    const next: ColorsParameterMap = { ...base };
    if (!remote) {
        return next;
    }

    // Handle nested {colors: {...}} structure from backend
    let colorsData: Record<string, ColorsElement | undefined> = remote as Record<string, ColorsElement | undefined>;
    const remoteWithColors = remote as { colors?: unknown };
    if (remoteWithColors.colors && typeof remoteWithColors.colors === 'object' && remoteWithColors.colors !== null) {
        colorsData = remoteWithColors.colors as Record<string, ColorsElement | undefined>;
    }

    Object.entries(colorsData).forEach(([key, value]) => {
        if (!value) {
            return;
        }
        const isLegacy = PALETTE_COLOR_KEYS.includes(key as PaletteColorKey) || DARK_OVERRIDE_KEYS.includes(key as DarkOverrideKey);
        const isModern = key.startsWith('--web-color-');

        if (isLegacy || isModern) {
            next[key as any] = { ...value } as ColorsElement;
        }
    });
    return next;
};


export const opAspectRatio = {
    'aspect_1-1': "Cuadrado",
    'aspect_4-3': "Horizontal 4:3",
    'aspect_3-2': "Horizontal 3:2",
    'aspect_8-5': "Horizontal 8:5",
    'aspect_16-9': "Horizontal 16:9",
    // 'aspect_25-11': "Horizontal 25:11",
    // 'aspect_5-1': "Horizontal 5:1",
    'aspect_3-4': "Vertical 4:3",
    'aspect_2-3': "Vertical 3:2",
    'aspect_5-8': "Vertical 8:5",
    'aspect_9-16': "Vertical 16:9",
    // 'aspect_11-25': "Vertical 25:11",
    // 'aspect_1-5': "Vertical 5:1",
}

export const verticalAspects = [
    'aspect_3-4',
    'aspect_2-3',
    'aspect_5-8',
    'aspect_9-16',
    'aspect_11-25',
    'aspect_1-5'
];

export const convertHarmonyToPalette = (config: ColorPaletteConfig, basePalette: ColorsParameterMap): ColorsParameterMap => {
    const count = config.colorCount;
    const customColors = [config.baseColor1, config.baseColor2, config.baseColor3]
        .map((color) => normalizeHex(color) ?? null)
        .filter((color): color is string => Boolean(color));

    const colors =
        config.harmony === 'custom'
            ? (customColors.length > 0 ? customColors : [config.baseColor1]).slice(0, count)
            : generateHarmony(config.baseColor1, config.harmony, count);

    // Modern Generation: Create all CSS variables directly in the palette map
    // Start with base palette to preserve Dark Overrides or other settings
    const next: ColorsParameterMap = { ...basePalette };

    // Clean up legacy keys to avoid DB clutter
    PALETTE_COLOR_KEYS.forEach((key) => {
        delete next[key];
    });

    // Create all CSS variables directly in the palette map
    colors.forEach((baseColor, index) => {
        const i = index + 1;
        const tones = generateTones(baseColor);

        const setVar = (suffix: string, value: string) => {
            next[`--web-color-${i}${suffix}`] = { codigo: value, has_change: true };
        };

        setVar('', tones.base);
        setVar('-c1', tones.c1);
        setVar('-c2', tones.c2);
        setVar('-o1', tones.o1);
        setVar('-o2', tones.o2);
    });

    return next;
};
